import { useEffect, useRef, useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import {
  PlusCircleSolidIcon, Button, Field, SelectField, TextareaField } from "@fawkes/ui";
import { Ban, Mail, Phone, Smartphone } from "lucide-react";
import {
  addressDetails,
  autocompleteAddress,
  lookupPostcode,
  type AddressDetails,
  type AddressSuggestion,
} from "../api/postcodeLookup";

// ── relation picker (shared by the intake + release wizards; parity slice 8) ──────────────────────
//
// Search mirrors the Jmix RelationFindFragment (name, or e-mail and/or postcode+huisnummer,
// AND-combined server-side); results render as the Jmix RelationSearchResultsFragment rich cards
// (name, two address lines, contact rows with icons, the "Op zwarte lijst" badge and a per-card
// "Selecteren"); the inline create form mirrors the Jmix RelationDetailsFragment (per-country Aanhef,
// Land with flags defaulting NL, Opmerking, and the postcode+huisnummer → straat/woonplaats lookup
// with its Bevestigen safety-net). Creation posts verbatim to the relatie module's own endpoint —
// the wizards never reimplement Klant/Adres/Contact creation.

/** A search hit from GET …/reference/klanten — the Jmix result-card facts. */
export interface KlantOption {
  id: number;
  label: string;
  adresRegel1: string | null;
  adresRegel2: string | null;
  telefoon: string | null;
  mobiel: string | null;
  email: string | null;
  geblokkeerd: boolean;
}

// Jmix RelationDetailsFragment.supportedCountries() — the stored value is the Dutch country NAME
// ("Nederland", not "NL"), exactly what Jmix writes into adres.land. Data, not translation.
const COUNTRIES: { code: string; name: string; flag: string }[] = [
  { code: "NL", name: "Nederland", flag: "🇳🇱" },
  { code: "BE", name: "Belgie", flag: "🇧🇪" },
  { code: "DE", name: "Duitsland", flag: "🇩🇪" },
  { code: "FR", name: "Frankrijk", flag: "🇫🇷" },
  { code: "ES", name: "Spanje", flag: "🇪🇸" },
  { code: "IT", name: "Italie", flag: "🇮🇹" },
  { code: "GB", name: "Verenigd Koninkrijk", flag: "🇬🇧" },
  { code: "US", name: "Verenigde Staten", flag: "🇺🇸" },
];

// Jmix RelationDetailsFragment.resolveSalutationOptions() — per-country Aanhef vocabularies.
const SALUTATIONS: Record<string, string[]> = {
  NL: ["Fam.", "Dhr.", "Mevr.", "Dhr./Mevr.", "t.a.v."],
  BE: ["Dhr.", "Mevr."],
  DE: ["Herr", "Frau", "Divers"],
  FR: ["M.", "Mme", "Mx"],
  ES: ["Sr.", "Sra.", "Mx."],
  IT: ["Sig.", "Sig.ra", "Mx."],
};
const DEFAULT_SALUTATIONS = ["Mr", "Ms", "Mx"];

/** Jmix resolveCountryCode: a 2-letter value is a code; otherwise match the name; default NL. */
function countryCodeForLand(land: string): string {
  const trimmed = land.trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const match = COUNTRIES.find((c) => c.name.toLowerCase() === trimmed.toLowerCase());
  return match ? match.code : "NL";
}

function salutationsForLand(land: string): string[] {
  return SALUTATIONS[countryCodeForLand(land)] ?? DEFAULT_SALUTATIONS;
}

// The inline "Nieuwe relatie" form mirrors the Jmix RelationDetailsFragment field set (person +
// optional organisation): bedrijfsnaam at the top switches the relation to ORGANISATIE, otherwise it
// is a PERSOON keyed on achternaam.
interface NewRelationForm {
  bedrijfsnaam: string;
  aanhef: string;
  voorletters: string;
  tussenvoegsel: string;
  achternaam: string;
  postcode: string;
  huisnummer: string;
  huisnummerToevoeging: string;
  straat: string;
  woonplaats: string;
  land: string;
  email: string;
  telefoon1: string;
  telefoon2: string;
  opmerking: string;
}

function emptyNewRelation(): NewRelationForm {
  return {
    bedrijfsnaam: "",
    aanhef: "",
    voorletters: "",
    tussenvoegsel: "",
    achternaam: "",
    postcode: "",
    huisnummer: "",
    huisnummerToevoeging: "",
    straat: "",
    woonplaats: "",
    land: "Nederland", // Jmix applyDefaultCountryIfEmpty — NL by default
    email: "",
    telefoon1: "",
    telefoon2: "",
    opmerking: "",
  };
}

/** The relation detail behind the edit phase (GET /api/relaties/{id}) — the fields the form edits. */
interface RelatieDetail {
  id: number;
  klantnaam: string | null;
  version: number | null;
  aanhef: string | null;
  voorletters: string | null;
  tussenvoegsel: string | null;
  achternaam: string | null;
  naamBedrijf: string | null;
  emailAdres: string | null;
  telefoon1: string | null;
  telefoon2: string | null;
  straat: string | null;
  huisnummer: string | null;
  huisnummerToevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
  land: string | null;
}

/**
 * Read a Dutch postcode (and an optional house number) straight out of what was typed into the single
 * "Zoek op adres" box, so a search works without first picking an autocomplete suggestion.
 * "7581WL 7", "7581 WL 7" and "7581wl" all resolve; anything else yields nothing.
 */
export function parseTypedAddress(term: string): { postcode: string; huisnummer: string } {
  const m = /(\d{4})\s*([a-z]{2})\b\s*([0-9]+[a-z]?)?/i.exec(term ?? "");
  if (!m) {
    return { postcode: "", huisnummer: "" };
  }
  return { postcode: `${m[1]}${m[2].toUpperCase()}`, huisnummer: m[3] ?? "" };
}

export function RelationPicker({
  label,
  valueId,
  valueLabel,
  onChange,
  canCreate,
  feedPath,
  noneLabel,
  onNone,
  autoOpen = false,
  variant = "inline",
  stepTitle,
  stepInfo,
  onBack,
  registerSave,
  onPhaseChange,
  onCreated,
  t,
}: {
  label: string;
  valueId: number | null;
  valueLabel: string | null;
  onChange: (id: number | null, label: string | null) => void;
  /**
   * "inline" (default) = the compact "value + Wijzigen" control with a dropdown panel.
   * "step" = the full-width Jmix relation sub-wizard: Zoek eigenaar (e-mail card + en/of +
   * adres-card) → Zoekresultaten (relation cards + "Nieuwe relatie aanmaken") → Nieuwe relatie form.
   * Ported from the Jmix relation-find / relation-search-results / relation-details fragments.
   */
  variant?: "inline" | "step";
  /** Step mode heading ("Zoek eigenaar") and sub-line ("Wie doet afstand?"). */
  stepTitle?: string;
  stepInfo?: string;
  /** Step mode: the wizard-level Terug affordance on the first (find) phase. */
  onBack?: () => void;
  /**
   * Step mode: hands the host a "save the edited relation" function (or null when nothing is being
   * edited), so the WIZARD's Bevestigen persists the owner edits — no separate save button.
   */
  registerSave?: (save: (() => void) | null) => void;
  /**
   * Reports the step-mode phase so the host can hide its own footer while this card shows its own
   * (Jmix's Zoek-vinder screen carries Terug / Geen vinder / Zoeken itself).
   */
  onPhaseChange?: (phase: "find" | "results" | "details" | "edit") => void;
  /**
   * Stapmodus: er is zojuist een relatie aangemaakt. De host stapt hierop door — je hebt net alle
   * gegevens ingevuld, dus die daarna nóg een keer laten controleren is een klik om niets.
   */
  onCreated?: () => void;
  // Whether the operator holds ROLE_RELATIE_EDIT — the relatie module's own create authority. Gated
  // because the wizard operator (intake/release authority) need not also be a relatie editor; the
  // "Nieuwe relatie" affordance is hidden when they cannot create, so the picker never triggers a 403.
  canCreate: boolean;
  // The wizard-authority klanten feed this picker searches ("intake/reference/klanten" or
  // "release/reference/klanten") — same query params + response shape on both.
  feedPath: string;
  // The explicit "Geen vinder" affordance (Jmix RelationFindFragment, STRAY finder only): selecting it
  // clears the relation — an empty finder already meant "geen vinder", this just makes it a button.
  noneLabel?: string;
  /** Fired when the operator presses that button — a deliberate "there is none", not an empty pick. */
  onNone?: () => void;
  /**
   * Inline mode: start with the search panel open (when nothing is picked yet). For a step whose whole
   * purpose IS picking the relation — the release wizard's eigenaarstap — a collapsed "Geen  Wijzigen"
   * line is a dead end that costs a click before you can even type.
   */
  autoOpen?: boolean;
  t: (k: string) => string;
}) {
  const [open, setOpen] = useState(autoOpen && variant === "inline" && valueId == null);
  // Step mode phase (Jmix relation sub-wizard): find → results → details (new) / edit (chosen).
  const [phase, setPhase] = useState<"find" | "results" | "details" | "edit">("find");
  // Edit phase: the chosen relation's optimistic-lock version + the save outcome.
  const [detailVersion, setDetailVersion] = useState<number | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const stepMode = variant === "step";
  // Single-field address autocomplete (Postcode.eu, step mode): the typed term, its suggestions, and
  // the human-readable line of the picked address (postcode/huisnummer land in the search criteria).
  const [addressTerm, setAddressTerm] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  // The term those suggestions belong to. Without it the list from an earlier keystroke stays on
  // screen while the newer request is still out — typing "7581WL" kept showing the answer to "7581",
  // a list of bare postcodes, which read as a broken search rather than a slow one.
  const [suggestionsFor, setSuggestionsFor] = useState("");
  // Set when the server reports it has no Postcode.eu credentials: the field then says so instead of
  // silently behaving like every address has no matches.
  const [addressUnavailable, setAddressUnavailable] = useState(false);
  const [addressPicked, setAddressPicked] = useState<string | null>(null);
  // The full resolved address behind `addressPicked`. The search criteria only carry postcode +
  // huisnummer, so without this the resolved straat/woonplaats would be dropped on the floor and the
  // "Nieuwe relatie" form would open with an address missing its street and city.
  const [addressResolved, setAddressResolved] = useState<AddressDetails | null>(null);
  // Where we are in the Postcode.eu hierarchy: "nld" until a non-Address match is picked, then that
  // match's context so the next suggestions are its children (a postcode's house numbers, …).
  const [addressContext, setAddressContext] = useState("nld");
  // Postcode.eu bills per autocomplete SESSION, not per keystroke — one id for this picker instance.
  const sessionId = useRef(`fawkes-${Math.random().toString(36).slice(2)}-${Date.now()}`).current;
  const [q, setQ] = useState("");
  const [email, setEmail] = useState("");
  const [postcode, setPostcode] = useState("");
  const [huisnummer, setHuisnummer] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<NewRelationForm>(emptyNewRelation);
  // Persoon of organisatie, zoals in het dialoog "Nieuwe relatie". Een nieuwe eigenaar is bijna altijd een persoon:
  // het formulier begon met "Bedrijfsnaam" en dat zette mensen op het verkeerde been (Alma, Roze, 2026-10-09).
  const [organisatie, setOrganisatie] = useState(false);
  // Whether the operator edited the auto-opened (zero-hits) create form — once touched it stops
  // re-syncing its prefill from the still-editable search criteria above it.
  const [formTouched, setFormTouched] = useState(false);
  const [createError, setCreateError] = useState<null | "forbidden" | "error">(null);

  const { mutate: createRelation, isLoading: creatingBusy } = useCustomMutation();
  // The Bevestigen safety-net lookup runs before the POST; block double-submits while it is in flight.
  const [submitBusy, setSubmitBusy] = useState(false);

  // Existing-relation lookup mirrors the Jmix RelationFindFragment: by name, or by e-mail and/or
  // postcode+huisnummer (AND-combined server-side). When nothing matches the operator can create a new
  // relation inline (Jmix relation sub-wizard create path), prefilled from the search criteria.
  // What was TYPED into "Zoek op adres" counts too. Postcode/huisnummer were only filled by picking
  // a suggestion, so typing "7581WL 7" and pressing Zoeken searched on nothing — the button even
  // stayed disabled. A typed Dutch postcode (+ optional house number) is read straight off the term.
  const getypt = parseTypedAddress(addressTerm);
  const zoekPostcode = postcode.trim() || getypt.postcode;
  const zoekHuisnummer = huisnummer.trim() || getypt.huisnummer;

  // Zonder zoekcriteria is er niets te zoeken. Belangrijk voor de feed hieronder: de klanten-lijst
  // ongefilterd opvragen levert een willekeurige greep uit honderdduizenden relaties — een muur van
  // naamloze adreskaartjes waar niemand iets aan heeft.
  const hasCriteria = !!(q.trim() || email.trim() || zoekPostcode || zoekHuisnummer);

  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (email.trim()) params.set("email", email.trim());
  if (zoekPostcode) params.set("postcode", zoekPostcode);
  if (zoekHuisnummer) params.set("huisnummer", zoekHuisnummer);
  const feed = useCustom({
    url: `${feedPath}?${params.toString()}`,
    method: "get",
    // Step mode searches on demand (the Jmix "Zoeken" button moves to the results phase); inline mode
    // keeps its live-as-you-type panel behaviour — maar pas zodra er iets ingevuld is.
    queryOptions: { enabled: stepMode ? phase === "results" : open && !creating && hasCriteria },
  });
  const results = (feed.data?.data as KlantOption[] | undefined) ?? [];
  // Zero-hits skip (Jmix: 0 results → straight to the prefilled create form). Only once the feed
  // settled (data present, not refetching) so transient empties while typing don't flash the form.
  const settledEmpty = hasCriteria && feed.data != null && !feed.isFetching && results.length === 0;
  const zeroHits = stepMode
    ? phase === "results" && settledEmpty
    : open && !creating && settledEmpty;
  const showCreateForm = stepMode ? phase === "details" : open && (creating || (zeroHits && canCreate));

  function reset() {
    setQ("");
    setEmail("");
    setPostcode("");
    setHuisnummer("");
    setCreating(false);
    setCreateError(null);
    setForm(emptyNewRelation());
    setFormTouched(false);
    setOrganisatie(false);
  }

  // Prefill the create form from whatever the operator already typed into the search (Jmix carries the
  // e-mail / postcode+huisnummer over into the new-relation details step).
  function prefillFromCriteria(): NewRelationForm {
    return {
      ...emptyNewRelation(),
      achternaam: q.trim(),
      email: email.trim(),
      postcode: postcode.trim(),
      huisnummer: huisnummer.trim(),
      // Straat/woonplaats/toevoeging come from the picked Postcode.eu address — they are not part of
      // the search criteria, so they would otherwise be lost between "Zoek op adres" and this form.
      huisnummerToevoeging: addressResolved?.buildingNumberAddition ?? "",
      straat: addressResolved?.street ?? "",
      woonplaats: addressResolved?.city ?? "",
    };
  }

  function startCreate() {
    setForm(prefillFromCriteria());
    setFormTouched(false);
    setCreateError(null);
    setCreating(true);
  }

  // The zero-hits auto-opened form keeps tracking the search criteria until the operator touches it
  // (the search inputs stay visible and editable above it).
  useEffect(() => {
    if (!zeroHits || creating || !canCreate || formTouched) return;
    setForm(prefillFromCriteria());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeroHits, creating, canCreate, formTouched, q, email, postcode, huisnummer, addressResolved]);

  // Step mode: a chosen relation opens the EDIT phase — the operator must be able to correct/complete
  // the owner's details right here (name, address, phone, e-mail) instead of only seeing a name.
  const detailFeed = useCustom({
    url: `relaties/${valueId ?? 0}`,
    method: "get",
    queryOptions: { enabled: stepMode && phase === "edit" && valueId != null },
  });

  useEffect(() => {
    if (!stepMode) return;
    // Ook vanuit "details": een zojuist aangemaakte relatie is de gekozen relatie, dus die hoort in de
    // bewerkfase te openen. Zonder "details" hier bleef het aanmaakformulier staan — leeggemaakt door
    // reset(), waardoor het leek alsof het aanmaken niets had gedaan.
    if (valueId != null && phase !== "edit") setPhase("edit");
    if (valueId == null && phase === "edit") setPhase("find");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepMode, valueId]);

  // Prefill the editable form from the chosen relation (response field names differ from the request's).
  useEffect(() => {
    if (!stepMode || phase !== "edit" || valueId == null) return;
    const d = detailFeed.data?.data as RelatieDetail | undefined;
    if (!d || d.id !== valueId) return;
    setForm({
      bedrijfsnaam: d.naamBedrijf ?? "",
      aanhef: d.aanhef ?? "",
      voorletters: d.voorletters ?? "",
      tussenvoegsel: d.tussenvoegsel ?? "",
      achternaam: d.achternaam ?? "",
      straat: d.straat ?? "",
      huisnummer: d.huisnummer ?? "",
      huisnummerToevoeging: d.huisnummerToevoeging ?? "",
      postcode: d.postcode ?? "",
      woonplaats: d.woonplaats ?? "",
      land: d.land ?? "Nederland",
      email: d.emailAdres ?? "",
      telefoon1: d.telefoon1 ?? "",
      telefoon2: d.telefoon2 ?? "",
      opmerking: "",
    });
    setDetailVersion(d.version ?? null);
    setSaveState("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepMode, phase, valueId, detailFeed.data]);

  /** Save the edited owner details (PUT /api/relaties/{id}) — the Jmix relatie tile save path. */
  function saveRelation() {
    if (valueId == null || saveState === "saving") return;
    setSaveState("saving");
    createRelation(
      {
        url: `relaties/${valueId}`,
        method: "put",
        values: {
          aanhef: form.aanhef.trim() || null,
          voorletters: form.voorletters.trim() || null,
          tussenvoegsel: form.tussenvoegsel.trim() || null,
          achternaam: form.achternaam.trim() || null,
          bedrijfsnaam: form.bedrijfsnaam.trim() || null,
          straat: form.straat.trim() || null,
          huisnummer: form.huisnummer.trim() || null,
          huisnummerToevoeging: form.huisnummerToevoeging.trim() || null,
          postcode: form.postcode.trim() || null,
          woonplaats: form.woonplaats.trim() || null,
          land: form.land.trim() || null,
          email: form.email.trim() || null,
          telefoon1: form.telefoon1.trim() || null,
          telefoon2: form.telefoon2.trim() || null,
          version: detailVersion,
        },
      },
      {
        onSuccess: (resp) => {
          const saved = resp?.data as RelatieDetail | undefined;
          if (saved) {
            setDetailVersion(saved.version ?? null);
            if (saved.klantnaam) onChange(valueId, saved.klantnaam);
          }
          setSaveState("saved");
        },
        onError: () => setSaveState("error"),
      },
    );
  }

  // Publish the save function while the edit phase is open so the wizard's Bevestigen persists the
  // owner edits; withdraw it as soon as the step leaves that phase.
  useEffect(() => {
    onPhaseChange?.(phase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (!registerSave) return;
    registerSave(stepMode && phase === "edit" && valueId != null ? saveRelation : null);
    return () => registerSave(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerSave, stepMode, phase, valueId, form, detailVersion]);

  // Address autocomplete, debounced 300ms (Jmix typed-address behaviour). Only in step mode, and only
  // while nothing is picked yet — picking replaces the list with the resolved address line.
  const addressSeq = useRef(0);
  useEffect(() => {
    if (!stepMode || phase !== "find") return;
    const term = addressTerm.trim();
    // Free-text search ALWAYS wins over the drilled-down context. Postcode.eu resolves a full term
    // ("Hogenhof 7 Losser", "7581WL 7", "Losser Hogenhof") straight to Address matches at country
    // level; querying a drilled context instead would answer with that context's children — which is
    // how typing a whole address used to come back as a list of bare 7581xx postcodes.
    // The context is therefore only used for the short follow-up query right after drilling in
    // (typing a house number into a picked street/postcode), where there is no term to search on.
    const drilled = addressContext !== "nld";
    const useContext = drilled && term.length < 3;
    const ctx = useContext ? addressContext : "nld";
    const minLen = useContext ? 0 : 3;
    if (addressPicked != null || term.length < minLen) {
      setAddressSuggestions([]);
      return;
    }
    const seq = ++addressSeq.current;
    const timer = window.setTimeout(() => {
      void autocompleteAddress(term, sessionId, ctx).then((res) => {
        if (seq !== addressSeq.current) return;
        setAddressUnavailable(res.unavailable);
        setAddressSuggestions(res.matches);
        setSuggestionsFor(term);
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [stepMode, phase, addressTerm, addressPicked, sessionId, addressContext]);

  /**
   * Pick one suggestion: resolve it to a full address and feed postcode + huisnummer into the relation
   * search criteria (and keep the readable line for the preview + the new-relation prefill).
   */
  async function pickAddress(s: AddressSuggestion) {
    // Not an address yet (country/city/street/postcode level) → drill INTO it: the next suggestions
    // come from this match's context. Picking "7581WL" then offers its house numbers.
    if (s.precision && s.precision !== "Address") {
      setAddressContext(s.context);
      setAddressTerm("");
      setAddressSuggestions([]);
      setAddressPicked(null);
      return;
    }
    const details = await addressDetails(s.context, sessionId);
    setAddressSuggestions([]);
    setAddressResolved(details);
    if (!details) {
      // No precision reported and nothing resolved — treat it as a drill-down step instead.
      setAddressContext(s.context);
      setAddressTerm("");
      return;
    }
    setPostcode(details.postcode ?? "");
    setHuisnummer(details.buildingNumber ?? "");
    const line = [
      [details.street, details.buildingNumber, details.buildingNumberAddition].filter(Boolean).join(" "),
      [details.postcode, details.city].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");
    setAddressPicked(line || s.label);
    setAddressTerm(line || s.label);
  }

  // Step mode: no hits → go straight to the "Nieuwe relatie" form with the searched address already
  // filled in (Jmix relation sub-wizard: the results phase hands its criteria to the details phase).
  useEffect(() => {
    if (!stepMode || phase !== "results" || !zeroHits || !canCreate) return;
    setForm(prefillFromCriteria());
    setFormTouched(false);
    setCreateError(null);
    setPhase("details");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepMode, phase, zeroHits, canCreate]);

  // Postcode + huisnummer → straat/woonplaats, debounced 350ms like the Jmix address autocomplete.
  // Only fills blank fields (Jmix applyAddressLookupResult), and skips entirely when the operator
  // already filled both.
  const lookupSeq = useRef(0);
  useEffect(() => {
    if (!showCreateForm) return;
    const pc = form.postcode.trim();
    const nr = form.huisnummer.trim();
    if (!pc || !nr) return;
    if (form.straat.trim() && form.woonplaats.trim()) return;
    const seq = ++lookupSeq.current;
    const timer = window.setTimeout(() => {
      void lookupPostcode(pc, nr, form.huisnummerToevoeging).then((res) => {
        if (!res || seq !== lookupSeq.current) return;
        setForm((f) => ({
          ...f,
          straat: f.straat.trim() ? f.straat : (res.straat ?? f.straat),
          woonplaats: f.woonplaats.trim() ? f.woonplaats : (res.woonplaats ?? f.woonplaats),
        }));
      });
    }, 350);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateForm, form.postcode, form.huisnummer, form.huisnummerToevoeging]);

  const canSubmitCreate = organisatie ? form.bedrijfsnaam.trim() !== "" : form.achternaam.trim() !== "";

  async function submitCreate() {
    if (!canSubmitCreate || submitBusy) {
      return;
    }
    const isOrg = organisatie;
    setCreateError(null);
    setSubmitBusy(true);
    // Bevestigen safety-net (Jmix runAddressLookupSynchronouslyIfNeeded): when straat/woonplaats is
    // still blank but postcode+huisnummer are present, try the lookup once more before saving.
    let straat = form.straat.trim();
    let woonplaats = form.woonplaats.trim();
    if ((!straat || !woonplaats) && form.postcode.trim() && form.huisnummer.trim()) {
      const res = await lookupPostcode(form.postcode, form.huisnummer, form.huisnummerToevoeging);
      if (res) {
        straat = straat || (res.straat ?? "").trim();
        woonplaats = woonplaats || (res.woonplaats ?? "").trim();
      }
    }
    createRelation(
      {
        url: "relaties",
        method: "post",
        values: {
          type: isOrg ? "ORGANISATIE" : "PERSOON",
          aanhef: form.aanhef.trim() || null,
          voorletters: form.voorletters.trim() || null,
          tussenvoegsel: form.tussenvoegsel.trim() || null,
          achternaam: form.achternaam.trim() || null,
          bedrijfsnaam: isOrg ? form.bedrijfsnaam.trim() || null : null,
          straat: straat || null,
          huisnummer: form.huisnummer.trim() || null,
          huisnummerToevoeging: form.huisnummerToevoeging.trim() || null,
          postcode: form.postcode.trim() || null,
          woonplaats: woonplaats || null,
          land: form.land.trim() || null,
          email: form.email.trim() || null,
          telefoon1: form.telefoon1.trim() || null,
          telefoon2: form.telefoon2.trim() || null,
          opmerking: form.opmerking.trim() || null,
        },
      },
      {
        onSuccess: (resp) => {
          setSubmitBusy(false);
          const created = resp?.data as { id: number; klantnaam: string } | undefined;
          if (created) {
            onChange(created.id, created.klantnaam);
            setOpen(false);
            reset();
            onCreated?.();
          }
        },
        onError: (err) => {
          setSubmitBusy(false);
          const code = (err as { statusCode?: number }).statusCode;
          setCreateError(code === 403 ? "forbidden" : "error");
        },
      },
    );
  }

  function setField(key: keyof NewRelationForm, value: string) {
    setFormTouched(true);
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Land switch (Jmix): the Aanhef vocabulary follows the country; a value that no longer fits is cleared.
  function setLand(value: string) {
    setFormTouched(true);
    setForm((f) => ({
      ...f,
      land: value,
      aanhef: salutationsForLand(value).includes(f.aanhef) ? f.aanhef : "",
    }));
  }

  function selectResult(r: KlantOption) {
    onChange(r.id, r.label);
    setOpen(false);
    reset();
  }

  const aanhefOptions = salutationsForLand(form.land);

  /** The "Nieuwe relatie" fields (Jmix RelationDetailsFragment order) — shared by both variants. */
  function renderCreateFields() {
    return (
      <>
        <div className="fawkes-relatie-create__type">
          <label className="fawkes-relatie-create__type-opt">
            <input type="radio" name="relation-picker-type" checked={!organisatie} onChange={() => setOrganisatie(false)} />
            <span>{t("relatieListView.type.persoon")}</span>
          </label>
          <label className="fawkes-relatie-create__type-opt">
            <input type="radio" name="relation-picker-type" checked={organisatie} onChange={() => setOrganisatie(true)} />
            <span>{t("relatieListView.type.organisatie")}</span>
          </label>
        </div>
        <div className="fawkes-inboeken__relation-create-grid">
          {organisatie && (
            <Field
              label={t("inboeken.relation.create.bedrijfsnaam")}
              value={form.bedrijfsnaam}
              onChange={(e) => setField("bedrijfsnaam", e.target.value)}
            />
          )}
          <SelectField
            label={t("inboeken.relation.create.aanhef")}
            value={form.aanhef}
            onChange={(e) => setField("aanhef", e.target.value)}
            options={[{ value: "", label: "" }, ...aanhefOptions.map((s) => ({ value: s, label: s }))]}
          />
          <Field
            label={t("inboeken.relation.create.voorletters")}
            value={form.voorletters}
            onChange={(e) => setField("voorletters", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.tussenvoegsel")}
            value={form.tussenvoegsel}
            onChange={(e) => setField("tussenvoegsel", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.achternaam")}
            value={form.achternaam}
            onChange={(e) => setField("achternaam", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.postcode")}
            value={form.postcode}
            onChange={(e) => setField("postcode", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.huisnummer")}
            value={form.huisnummer}
            onChange={(e) => setField("huisnummer", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.toevoeging")}
            value={form.huisnummerToevoeging}
            onChange={(e) => setField("huisnummerToevoeging", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.straat")}
            value={form.straat}
            onChange={(e) => setField("straat", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.woonplaats")}
            value={form.woonplaats}
            onChange={(e) => setField("woonplaats", e.target.value)}
          />
          <SelectField
            label={t("inboeken.relation.create.land")}
            value={form.land}
            onChange={(e) => setLand(e.target.value)}
            options={COUNTRIES.map((c) => ({ value: c.name, label: `${c.flag} ${c.name}` }))}
          />
          <Field
            label={t("inboeken.relation.create.email")}
            type="email"
            invalidEmailMessage={t("validatie.emailadres")}
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.telefoon")}
            value={form.telefoon1}
            onChange={(e) => setField("telefoon1", e.target.value)}
          />
          <Field
            label={t("inboeken.relation.create.telefoon2")}
            value={form.telefoon2}
            onChange={(e) => setField("telefoon2", e.target.value)}
          />
        </div>
        <TextareaField
          label={t("inboeken.relation.create.opmerking")}
          value={form.opmerking}
          onChange={(e) => setField("opmerking", e.target.value)}
          rows={2}
        />
        {createError && (
          <p className="fawkes-inboeken__notice" role="alert">
            {t(
              createError === "forbidden"
                ? "inboeken.relation.create.forbidden"
                : "inboeken.relation.create.error",
            )}
          </p>
        )}
      </>
    );
  }

  /** The relation result cards (Jmix RelationSearchResultsFragment) — shared by both variants. */
  function renderResultCards() {
    return results.map((r) => (
      <li key={r.id} className="fawkes-relation-card">
        <div className="fawkes-relation-card__body">
          <span className="fawkes-relation-card__name">{r.label}</span>
          {r.adresRegel1 && <span className="fawkes-relation-card__line">{r.adresRegel1}</span>}
          {r.adresRegel2 && <span className="fawkes-relation-card__line">{r.adresRegel2}</span>}
          {r.mobiel && (
            <span className="fawkes-relation-card__contact">
              <Smartphone size={14} aria-hidden="true" />
              {r.mobiel}
            </span>
          )}
          {r.telefoon && (
            <span className="fawkes-relation-card__contact">
              <Phone size={14} aria-hidden="true" />
              {r.telefoon}
            </span>
          )}
          {r.email && (
            <span className="fawkes-relation-card__contact">
              <Mail size={14} aria-hidden="true" />
              {r.email}
            </span>
          )}
          {r.geblokkeerd && (
            <span className="fawkes-relation-card__badge">
              <Ban size={13} aria-hidden="true" />
              {t("inboeken.relation.blocked")}
            </span>
          )}
        </div>
        <div className="fawkes-relation-card__actions">
          <Button variant="secondary" onClick={() => selectResult(r)}>
            {t("inboeken.relation.select")}
          </Button>
        </div>
      </li>
    ));
  }

  // ── step variant: the full Jmix relation sub-wizard (find → results → details) ──────────────────
  if (stepMode) {
    return (
      <div className="fawkes-relstep">
        {phase === "find" && (
          <>
            <div className="fawkes-relstep__head">
              <h3 className="fawkes-relstep__title">{stepTitle ?? label}</h3>
              {stepInfo && <p className="fawkes-relstep__info">{stepInfo}</p>}
            </div>
            <div className="fawkes-relstep__card">
              <img className="fawkes-relstep__card-icon" src="/intake/icon_relation_email.svg" alt="" />
              <div className="fawkes-relstep__card-body">
                <span className="fawkes-relstep__card-title">{t("inboeken.relation.emailSearch.title")}</span>
                <input
                  className="fui-field__input"
                  value={email}
                  placeholder={t("inboeken.relation.email")}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <span className="fawkes-relstep__separator">{t("inboeken.relation.separator")}</span>
            <div className="fawkes-relstep__card">
              <img className="fawkes-relstep__card-icon" src="/intake/icon_relation_address.svg" alt="" />
              <div className="fawkes-relstep__card-body">
                <span className="fawkes-relstep__card-title">{t("inboeken.relation.addressSearch.title")}</span>
                {/* One address field (Jmix/Postcode.eu autocomplete): type street/city/postcode, pick a
                    suggestion, and postcode + huisnummer are filled from the resolved address. */}
                <div className="fawkes-relstep__autocomplete">
                  <input
                    className="fui-field__input"
                    value={addressTerm}
                    placeholder={t("inboeken.relation.addressSearch.placeholder")}
                    onChange={(e) => {
                      setAddressTerm(e.target.value);
                      setAddressPicked(null);
                      // Typing again invalidates the previously resolved address, so its street/city
                      // can't leak into a create form for a different address.
                      setAddressResolved(null);
                      // Emptying the field starts over at country level (otherwise you stay trapped
                      // inside the last drilled-down context).
                      if (!e.target.value.trim()) setAddressContext("nld");
                    }}
                  />
                  {/* Only show a list that belongs to what is currently typed. */}
                  {addressSuggestions.length > 0
                    && addressPicked == null
                    && suggestionsFor === addressTerm.trim() && (
                    <ul className="fawkes-relstep__suggestions">
                      {addressSuggestions.map((s) => (
                        <li key={s.context}>
                          <button type="button" onClick={() => void pickAddress(s)}>
                            <span className="fawkes-relstep__suggestion-label">{s.label}</span>
                            {s.description && (
                              <span className="fawkes-relstep__suggestion-desc">{s.description}</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {addressUnavailable && (
                    <span className="fawkes-inboeken__field-error" role="status">
                      {t("inboeken.relation.addressSearch.unavailable")}
                    </span>
                  )}
                  {addressPicked && (
                    <span className="fawkes-relstep__address-preview">{addressPicked}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="fawkes-relstep__actions">
              {/* Jmix gives this footer three equal filled buttons — Terug / Geen vinder / Zoeken —
                  rather than mixing outline and filled. */}
              {onBack && (
                <Button variant="primary" onClick={onBack}>
                  ‹ {t("common.back")}
                </Button>
              )}
              <span className="fawkes-relstep__actions-spacer" />
              {noneLabel && (
                <Button
                  variant="primary"
                  onClick={() => {
                    onChange(null, null);
                    // "Geen vinder" is a DECISION, not merely an empty selection — the stray flow skips
                    // its vindersverklaring only when the operator says so here.
                    onNone?.();
                  }}
                >
                  {noneLabel}
                </Button>
              )}
              {/* Second spacer so the escape sits centred between Terug and Zoeken, as in Jmix. */}
              {noneLabel && <span className="fawkes-relstep__actions-spacer" />}
              <Button variant="primary" disabled={!hasCriteria} onClick={() => setPhase("results")}>
                {t("inboeken.relation.searchAction")} ›
              </Button>
            </div>
          </>
        )}

        {phase === "results" && (
          <>
            <div className="fawkes-relstep__title-row">
              <button
                type="button"
                className="fawkes-relstep__back"
                aria-label={t("common.back")}
                onClick={() => setPhase("find")}
              >
                ‹
              </button>
              <h3 className="fawkes-relstep__title">{t("inboeken.relation.results.title")}</h3>
            </div>
            <p className="fawkes-relstep__info">{t("inboeken.relation.results.info")}</p>
            {feed.isFetching ? (
              <p className="fawkes-inboeken__muted">{t("common.loading")}</p>
            ) : results.length === 0 ? (
              <p className="fawkes-inboeken__muted" role="status">
                {t("inboeken.relation.results.empty")}
              </p>
            ) : (
              <ul className="fawkes-relation-results">{renderResultCards()}</ul>
            )}
            {canCreate && (
              <Button
                variant="primary"
                onClick={() => {
                  setForm(prefillFromCriteria());
                  setFormTouched(false);
                  setCreateError(null);
                  setPhase("details");
                }}
              >
                <PlusCircleSolidIcon width={18} height={18} /> {t("inboeken.relation.create.start")}
              </Button>
            )}
          </>
        )}

        {phase === "edit" && (
          <>
            <div className="fawkes-relstep__head">
              <h3 className="fawkes-relstep__title">{valueLabel ?? label}</h3>
              <p className="fawkes-relstep__info">{t("inboeken.relation.edit.info")}</p>
            </div>
            {detailFeed.isFetching && detailVersion == null ? (
              <p className="fawkes-inboeken__muted">{t("common.loading")}</p>
            ) : (
              renderCreateFields()
            )}
            {saveState === "saved" && (
              <p className="fawkes-inboeken__helper" role="status">
                {t("inboeken.relation.edit.saved")}
              </p>
            )}
            {saveState === "error" && (
              <p className="fawkes-inboeken__notice" role="alert">
                {t("inboeken.relation.edit.error")}
              </p>
            )}
          </>
        )}

        {phase === "details" && (
          <>
            <div className="fawkes-relstep__head">
              <h3 className="fawkes-relstep__title">{t("inboeken.relation.create.title")}</h3>
              {stepInfo && <p className="fawkes-relstep__info">{stepInfo}</p>}
            </div>
            {renderCreateFields()}
            <div className="fawkes-relstep__actions">
              <Button variant="secondary" onClick={() => setPhase("results")}>
                ‹ {t("common.back")}
              </Button>
              <span className="fawkes-relstep__actions-spacer" />
              <Button
                variant="primary"
                onClick={() => void submitCreate()}
                disabled={!canSubmitCreate || creatingBusy || submitBusy}
              >
                {t("inboeken.relation.create.submit")} ›
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="fawkes-inboeken__relation">
      <span className="fui-field__label">{label}</span>
      <div className="fawkes-inboeken__relation-control">
        <span className="fawkes-inboeken__relation-value">
          {valueLabel || t("inboeken.relation.none")}
        </span>
        <button
          type="button"
          className="fawkes-inboeken__link"
          onClick={() => {
            setOpen((o) => !o);
            setCreating(false);
            setCreateError(null);
            setFormTouched(false);
          }}
        >
          {t(open ? "inboeken.relation.close" : "inboeken.relation.change")}
        </button>
        {valueId != null && (
          <button type="button" className="fawkes-inboeken__link" onClick={() => onChange(null, null)}>
            {t("inboeken.relation.clear")}
          </button>
        )}
      </div>
      {open && !creating && (
        <div className="fawkes-inboeken__relation-panel">
          <input
            className="fui-field__input"
            value={q}
            placeholder={t("inboeken.relation.search")}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          <div className="fawkes-inboeken__relation-address">
            <input
              className="fui-field__input"
              value={email}
              placeholder={t("inboeken.relation.email")}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              className="fui-field__input"
              value={postcode}
              placeholder={t("inboeken.relation.postcode")}
              onChange={(e) => setPostcode(e.target.value)}
            />
            <input
              className="fui-field__input"
              value={huisnummer}
              placeholder={t("inboeken.relation.huisnummer")}
              onChange={(e) => setHuisnummer(e.target.value)}
            />
          </div>
          {!hasCriteria && (
            <p className="fawkes-inboeken__muted" role="status">
              {t("inboeken.relation.search.hint")}
            </p>
          )}
          {zeroHits && (
            <p className="fawkes-inboeken__muted" role="status">
              {t("inboeken.relation.results.empty")}
            </p>
          )}
          {hasCriteria && <ul className="fawkes-relation-results">{renderResultCards()}</ul>}
          {noneLabel && (
            <button
              type="button"
              className="fawkes-inboeken__link"
              onClick={() => {
                onChange(null, null);
                setOpen(false);
                reset();
              }}
            >
              {noneLabel}
            </button>
          )}
          {canCreate && !showCreateForm && (
            <button type="button" className="fawkes-inboeken__link" onClick={startCreate}>
              {t("inboeken.relation.create.start")}
            </button>
          )}
        </div>
      )}
      {showCreateForm && (
        <div className="fawkes-inboeken__relation-panel fawkes-inboeken__relation-create">
          <span className="fui-field__label">{t("inboeken.relation.create.title")}</span>
          {renderCreateFields()}
          <div className="fawkes-inboeken__relation-create-actions">
            <Button
              variant="primary"
              onClick={() => void submitCreate()}
              disabled={!canSubmitCreate || creatingBusy || submitBusy}
            >
              {t("inboeken.relation.create.submit")}
            </Button>
            {creating && (
              <Button
                variant="secondary"
                onClick={() => {
                  setCreating(false);
                  setCreateError(null);
                }}
              >
                {t("common.cancel")}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
